import { useMemo } from 'react';
import { useFundingPaymentsView } from '#/features/perpetuals/trade-overview/funding-payments-context';
import { useTranslation } from '#/features/localization/utils';

import { Loadable } from '#/components/shared/Loadable';

import { TableSpec, createTradeOverviewTable } from './table';
import { Empty, Error, Mono, ValueChange, ValuePair } from './atoms';

interface Props {
  tableSpec: TableSpec<string>;
}

export default function FundingPayments(props: Props) {
  const { tableSpec } = props;
  const [Table, Row] = useMemo(
    () => createTradeOverviewTable(tableSpec),
    [tableSpec],
  );
  const { status, error, fundingPayments } = useFundingPaymentsView();
  const { t } = useTranslation();

  if (status === 'error') {
    return <Error>{error}</Error>;
  }

  const isEmpty = fundingPayments.length === 0;
  const isLoading = status === 'loading' || status === 'disconnected';

  if (isEmpty) {
    return (
      <Loadable isLoading={isLoading}>
        <Empty>{t('You have no funding payments')}</Empty>
      </Loadable>
    );
  }

  return (
    <Loadable isLoading={isLoading}>
      <Table>
        {fundingPayments.map((fundingPayment) => (
          <Row
            key={fundingPayment.id}
            time={
              <ValuePair
                primary={fundingPayment.createdTime}
                secondary={fundingPayment.createdDate}
              />
            }
            market={fundingPayment.market}
            payment={
              <ValueChange direction={fundingPayment.paymentChangeDirection}>
                <Mono>{fundingPayment.payment}</Mono>
              </ValueChange>
            }
          />
        ))}
      </Table>
    </Loadable>
  );
}
